import { keepTwoOrLine } from '@/utils/utils';
import {
  V2EnergyReportElectricityDetailPostResponse,
  V2EnergyReportTrendPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { DateType } from '../../const';

export const getElecChartOption = (
  data?: V2EnergyReportElectricityDetailPostResponse,
  timeResolution?: DateType,
  showPhotovoltaic = false
) => {
  // const unit = data?.list?.[0]?.unitName
  const unit = 'kWh';
  const formatStr = timeResolution === DateType.DAY ? 'MM-DD' : timeResolution === DateType.MONTH ? 'YYYY-MM' : 'YYYY';
  const valueIsOverTenThousand = data?.list?.some(item => (item.value ?? 0) > 10000);
  const pvValueIsOverTenThousand = data?.list?.some(item => (item.pvValue ?? 0) > 10000);
  const useWanUnit = valueIsOverTenThousand && (!showPhotovoltaic || pvValueIsOverTenThousand);
  const showPlan = timeResolution === DateType.MONTH || timeResolution === DateType.YEAR;
  const temperatureName = timeResolution === DateType.DAY ? '当日最高气温' : '平均气温';

  return {
    backgroundColor: 'transparent',
    grid: {
      left: 80,
      right: 40,
      top: 60,
      bottom: 10,
      containLabel: true,
    },
    legend: {
      right: 20,
      top: 0,
      bottom: 20,
      itemWidth: 16, // 宽度
      itemHeight: 10, // 高度
      data: [
        ...(showPlan
          ? [
              {
                name: '计划用电量',
                icon: 'rect',
              },
            ]
          : []),
        {
          name: '用电量',
          icon: 'rect',
        },
        ...(showPhotovoltaic
          ? [
              {
                name: '光伏发电',
                icon: 'rect',
              },
            ]
          : []),
        {
          name: temperatureName,
          icon: 'path://M0 0 L16 0 L16 2 L0 2 Z',
        },
      ],
    },
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue).format(formatStr);
        let otherStr = '';
        let str = '';
        items.forEach((item: any) => {
          const { data, marker, seriesName } = item;
          otherStr += `<div style="margin-top:8px;width:220px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${
            !isNil(data?.[1]) ? keepTwoOrLine(data?.[1]) + data?.[2] : '-'
          }</div></div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },

    color: [showPlan ? '#FF9247' : undefined, '#2D8DFF', showPhotovoltaic ? '#59D744' : undefined, '#FFCB47'].filter(
      Boolean
    ),
    xAxis: [
      {
        type: 'time',
        minInterval:
          timeResolution === DateType.YEAR
            ? 365 * 24 * 3600 * 1000 // 按年：最小间隔1年
            : timeResolution === DateType.MONTH
            ? 30 * 24 * 3600 * 1000 // 按月：最小间隔1个月
            : 24 * 3600 * 1000, // 按日：最小间隔1天
        axisLabel: {
          formatter: function (e: number) {
            return dayjs(e).format(formatStr);
          },
          hideOverlap: true,
        },
        boundaryGap: true, // 或直接删掉
      },
    ],
    yAxis: [
      {
        type: 'value',
        alignTicks: true,
        name: `${showPhotovoltaic ? '用电量&光伏发电' : '用电量'}${useWanUnit ? `（万${unit}）` : `（${unit}）`}`,
        nameTextStyle: {
          fontWeight: 500,
          fontSize: 16,
        },
      },
      {
        type: 'value',
        alignTicks: true,
        name: '温度（℃）',
      },
    ],
    series: [
      ...(showPlan
        ? [
            {
              type: 'bar' as const,
              name: '计划用电量',
              barMaxWidth: 20,
              data: useWanUnit
                ? (data?.list ?? []).map(item => [
                    item.ts,
                    !isNil(item?.planValue) ? item.planValue / 10000 : null,
                    `万${unit}`,
                  ])
                : (data?.list ?? []).map(item => [item.ts, item.planValue, unit]),
            },
          ]
        : []),
      {
        type: 'bar',
        name: '用电量',
        stack: showPhotovoltaic ? 'useElec' : undefined,
        barMaxWidth: 20,
        data: useWanUnit
          ? (data?.list ?? []).map(item => [item.ts, !isNil(item?.value) ? item.value / 10000 : null, `万${unit}`])
          : (data?.list ?? []).map(item => [item.ts, item.value, unit]),
      },
      ...(showPhotovoltaic
        ? [
            {
              type: 'bar' as const,
              name: '光伏发电',
              stack: 'useElec',
              barMaxWidth: 20,
              data: useWanUnit
                ? (data?.list ?? []).map(item => [
                    item.ts,
                    !isNil(item?.pvValue) ? item.pvValue / 10000 : null,
                    `万${unit}`,
                  ])
                : (data?.list ?? []).map(item => [item.ts, item.pvValue, unit]),
            },
          ]
        : []),
      {
        type: 'line',
        name: temperatureName,
        data: (data?.list ?? []).map(item => [item.ts, item.temperature, '℃']),
        yAxisIndex: 1,
        // connectNulls: true,
        smooth: true,
        symbol: 'circle',
        symbolSize: 2,
        // showSymbol: false, // 只有在 tooltip hover 的时候显示symbol
        itemStyle: {
          // symbol样式
          normal: {
            borderColor: 'rgba(255, 255, 255, 0.2)', // symbol边框颜色
            borderWidth: 6, // symbol边框宽度
          },
        },
        lineStyle: {
          width: 2,
          type: 'solid', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
        },
      },
    ],
  };
};
export const getElecCostTrendChartOption = (data?: V2EnergyReportTrendPostResponse) => {
  const valueIsOverTenThousand = data?.list
    ?.map(i => i.list?.map(c => c.value))
    .flat()
    .some(item => (item ?? 0) > 10000);
  return {
    backgroundColor: 'transparent',
    grid: {
      left: 40,
      right: 40,
      top: 30,
      bottom: 10,
      containLabel: true,
    },
    legend: {
      right: 0,
      top: 0,
      bottom: 0,
    },
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = axisValue;
        let otherStr = '';
        let str = '';
        items.forEach((item: any) => {
          const { data, marker, seriesName } = item;
          otherStr += `<div style="margin-top:8px;width:260px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${
            !isNil(data?.[1]) ? keepTwoOrLine(data?.[1]) + data?.[2] : '-'
          }</div></div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },
    color: ['#FFCB47', '#16DD8E', '#2D8DFF'],
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          hideOverlap: true,
        },
        boundaryGap: true, // 或直接删掉
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: valueIsOverTenThousand ? '万元' : '元',
      },
    ],
    series: (data?.list ?? []).map(item => ({
      type: 'line',
      name: `${item.year}年度电费累积`,
      data: valueIsOverTenThousand
        ? (item?.list ?? []).map(item => [item.ts, !isNil(item?.value) ? item.value / 10000 : null, '万元'])
        : (item?.list ?? []).map(item => [item.ts, item.value, '元']),
      // connectNulls: true,
      smooth: true,
      symbol: 'circle',
      symbolSize: 2,
      // showSymbol: false, // 只有在 tooltip hover 的时候显示symbol
      itemStyle: {
        // symbol样式
        normal: {
          borderColor: 'rgba(255, 255, 255, 0.2)', // symbol边框颜色
          borderWidth: 6, // symbol边框宽度
        },
      },
      lineStyle: {
        width: 2,
        type: 'solid', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
      },
    })),
  };
};
